import DateTime from '../DateTime'

import isInstanceOfObjectId from '../utils/objectId/isInstanceOf'

import stringSearchTerm from './stringSearchTerm'

const NoValueComparators = [
    'list.empty',
    'list.notEmpty',
    'string.exists',
    'string.notExists',
    'number.exists',
    'number.notExists',
    'date.exists',
    'date.notExists',
    'bool.isTrue',
    'bool.isFalse',
    'bool.exists',
    'bool.notExists',
]

const compareRequiresValue = compare => {
    return !NoValueComparators.includes(compare)
}

const normalizeStringValue = (value, caseSensitive) => {
    if (typeof value !== 'string' || caseSensitive) {
        return value
    }

    if (value === '') {
        return value
    }

    return stringSearchTerm(value, 'like')
}

const convertItem = ({ field, compare, value, caseSensitive }, { nowDate = new DateTime.Date() } = {}) => {
    if (
        !compare ||
        (compare && (value === undefined || value === null || value === '') && compareRequiresValue(compare))
    ) {
        return null
    }

    const isValueObjectId = isInstanceOfObjectId(value)

    switch (compare) {
        // -- list
        case 'list.contains':
            return { [field]: typeof value === 'string' ? normalizeStringValue(value, caseSensitive) : value }
        case 'list.notContains':
            return {
                [field]: {
                    [isValueObjectId ? '$ne' : '$not']:
                        typeof value === 'string' ? normalizeStringValue(value, caseSensitive) : value,
                },
            }
        case 'list.empty':
            return { $or: [{ [field]: { $size: 0 } }, { [field]: { $exists: false } }, { [field]: null }] }
        case 'list.notEmpty':
            return { $and: [{ [field]: { $not: { $size: 0 } } }, { [field]: { $exists: true } }] }
        // -- string
        case 'string.equals':
            return { [field]: normalizeStringValue(value, caseSensitive) }
        case 'string.notEquals':
            return {
                [field]: {
                    [isValueObjectId ? '$ne' : '$not']:
                        typeof value === 'string' ? normalizeStringValue(value, caseSensitive) : value,
                },
            }
        case 'string.contains':
        case 'string.startsWith':
        case 'string.endsWith':
            return { [field]: stringSearchTerm(value, compare.split('.')[1]) }
        case 'string.notContains':
            return {
                [field]: {
                    [isValueObjectId ? '$ne' : '$not']:
                        typeof value === 'string' ? stringSearchTerm(value, 'contains') : value,
                },
            }
        case 'string.exists':
            return {
                $and: [{ [field]: { $exists: true } }, { [field]: { $ne: null } }, { [field]: { $ne: '' } }],
            }
        case 'string.notExists':
            return { $or: [{ [field]: { $exists: false } }, { [field]: null }, { [field]: '' }] }
        // -- number
        case 'number.equals':
            return { [field]: value }
        case 'number.notEquals':
            return { [field]: { $ne: value } }
        case 'number.greaterThan':
            return { [field]: { $gt: value } }
        case 'number.lessThan':
            return { [field]: { $lt: value } }
        case 'number.exists':
            return { $and: [{ [field]: { $exists: true } }, { [field]: { $ne: null } }] }
        case 'number.notExists':
            return { $or: [{ [field]: { $exists: false } }, { [field]: null }] }
        // -- date
        case 'date.exactlyDaysAgo':
            return {
                [field]: {
                    $gte: new DateTime.Date(nowDate.subtract(value, 'days')),
                    $lt: new DateTime.Date(nowDate.subtract(value, 'days').add(1, 'day')),
                },
            }
        case 'date.moreThanDaysAgo':
            return {
                [field]: {
                    $lt: new DateTime.Date(nowDate.subtract(value, 'days')),
                },
            }
        case 'date.lessThanDaysAgo':
            return {
                [field]: {
                    $gte: new DateTime.Date(nowDate.subtract(value, 'days')),
                    $lt: new DateTime.Date(nowDate).add(1, 'day'),
                },
            }
        case 'date.moment':
            switch (value) {
                case 'beforeToday':
                    return {
                        [field]: {
                            $lt: new DateTime.Date(nowDate),
                        },
                    }
                case 'beforeOrAtToday':
                    return {
                        [field]: {
                            $lt: new DateTime.Date(nowDate).add(1, 'day'),
                        },
                    }
                case 'afterToday':
                    return {
                        [field]: {
                            $gte: new DateTime.Date(nowDate).add(1, 'day'),
                        },
                    }
                case 'afterOrAtToday':
                    return {
                        [field]: {
                            $gte: new DateTime.Date(nowDate),
                        },
                    }
                default:
                    throw new Error(`Unknown value "${value} for date.moment operator.`)
            }
        case 'date.after':
            return {
                [field]: {
                    $gt: value,
                },
            }
        case 'date.before':
            return {
                [field]: {
                    $lt: value,
                },
            }
        case 'date.equals':
            return {
                [field]: {
                    $eq: value,
                },
            }
        case 'date.exists':
            return { $and: [{ [field]: { $exists: true } }, { [field]: { $ne: null } }] }
        case 'date.notExists':
            return { $or: [{ [field]: { $exists: false } }, { [field]: null }] }
        // -- bool
        case 'bool.isTrue':
            return { [field]: true }
        case 'bool.isFalse':
            return { [field]: false }
        case 'bool.exists':
            return { $or: [{ [field]: true }, { [field]: false }] }
        case 'bool.notExists':
            return { $and: [{ [field]: { $ne: true } }, { [field]: { $ne: false } }] }
        default:
            throw new Error(`Unknown comparator "${compare}" for field "${field}".`)
    }
}

const convertGroup = (filterGroup, settings) => {
    if (!filterGroup.fields || !filterGroup.fields.length) {
        return null
    }

    const convertedItems = filterGroup.fields.map(item => convertItem(item, settings)).filter(x => !!x)
    if (convertedItems.length === 0) {
        return null
    }

    if (convertedItems.length === 1) {
        return convertedItems[0]
    }

    return {
        [`$${filterGroup.operator}`]: convertedItems,
    }
}

function convertFilterToQuery(filter, settings) {
    if (!filter || !filter.groups) {
        return {}
    }

    const convertedGroups = (filter.groups || []).map(group => convertGroup(group, settings)).filter(x => !!x)
    if (!convertedGroups.length) {
        return {}
    }

    if (convertedGroups.length === 1) {
        return convertedGroups[0]
    }

    return {
        [`$${filter.operator}`]: convertedGroups,
    }
}

convertFilterToQuery.compareRequiresValue = compareRequiresValue

export default convertFilterToQuery
